import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ArrowRight, ArrowLeft, Check,
  User, Repeat, Users, Layers,
  Calendar as CalendarIcon, Clock, Sparkles, Loader2,
  Search, Mail, Globe,
} from 'lucide-react';
import { toLocalInputValue, localInputToUtcIso, parseUtc, guessUserTz } from '../lib/calendarDate';
import DateTimePickerFr from './DateTimePickerFr';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_CARDS = [
  {
    id: 'private_1on1',
    label: '1-to-1 privé',
    icon: User,
    tint: 'violet',
    description: 'Session individuelle sur invitation',
    examples: 'Mentorat personnalisé, conseil entre pros',
  },
  {
    id: 'public_slot',
    label: 'Slot public',
    icon: Repeat,
    tint: 'teal',
    description: 'Créneau ouvert à la réservation',
    examples: 'Comme un calendrier de médecin — pour une personne à la fois',
  },
  {
    id: 'public_event',
    label: 'Event public',
    icon: Users,
    tint: 'amber',
    description: 'Événement collectif visible sur ton profil',
    examples: 'Webinar, atelier de groupe, conférence',
  },
  {
    id: 'public_series',
    label: 'Série récurrente',
    icon: Layers,
    tint: 'cyan',
    description: 'Sessions répétées automatiquement',
    examples: 'Formation, accompagnement long',
  },
];

const TINT_CLASSES = {
  teal:   { bg: 'bg-cyan-500/10',   border: 'border-cyan-400/40',   text: 'text-cyan-300',   ring: 'ring-cyan-400' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-400/40', text: 'text-violet-300', ring: 'ring-violet-400' },
  amber:  { bg: 'bg-amber-500/10',  border: 'border-amber-400/40',  text: 'text-amber-300',  ring: 'ring-amber-400' },
  cyan:   { bg: 'bg-cyan-500/10',   border: 'border-cyan-400/40',   text: 'text-cyan-300',   ring: 'ring-cyan-400' },
};

const DAYS = [
  { value: 0, label: 'Lun' },
  { value: 1, label: 'Mar' },
  { value: 2, label: 'Mer' },
  { value: 3, label: 'Jeu' },
  { value: 4, label: 'Ven' },
  { value: 5, label: 'Sam' },
  { value: 6, label: 'Dim' },
];

const initialFormFor = (type) => {
  const now = new Date();
  // Default: next round hour, 60-min duration
  const start = new Date(now);
  start.setHours(start.getHours() + 1, 0, 0, 0);
  const end = new Date(start);
  end.setHours(end.getHours() + 1);
  return {
    type,
    title: '',
    description: '',
    start_local: toLocalInputValue(start),
    end_local: toLocalInputValue(end),
    max_capacity: type === 'public_event' ? 10 : 1,
    invited_user_id: '',
    invited_user_display_name: '',
    invitation_email: '',
    recurrence_pattern: type === 'public_slot' ? 'none' : (type === 'public_series' ? 'weekly' : 'none'),
    days_of_week: [now.getDay() === 0 ? 6 : now.getDay() - 1],  // today as ISO weekday
    series_occurrences: 6,
    series_can_book_full: true,
    series_can_book_per_session: true,
  };
};

/** Hydrate a form from an existing event (edit mode). Mirrors initialFormFor shape. */
const formFromEvent = (event) => {
  const start = parseUtc(event.start_time);
  const end = parseUtc(event.end_time);
  const rec = event.recurrence || {};
  return {
    type: event.type,
    title: event.title || '',
    description: event.description || '',
    start_local: toLocalInputValue(start),
    end_local: toLocalInputValue(end),
    max_capacity: event.max_capacity || 1,
    invited_user_id: event.invited_user_id || '',
    invited_user_display_name: '',  // backend doesn't return it; user can re-search if needed
    invitation_email: event.invitation_email || '',
    recurrence_pattern: rec.pattern || 'none',
    days_of_week: rec.days_of_week || [],
    series_occurrences: rec.occurrences_count || 6,
    series_can_book_full: event.series_config?.can_book_full_series ?? true,
    series_can_book_per_session: event.series_config?.can_book_per_session ?? true,
  };
};

// ============================================================================
// InviteePicker — Live search by name with email fallback (sprint 1.0b-β fix)
// ============================================================================

const InviteePicker = ({ form, updateForm }) => {
  const [query, setQuery] = useState(form.invited_user_display_name || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Sync external resets (e.g. wizard re-open)
  useEffect(() => {
    setQuery(form.invited_user_display_name || '');
  }, [form.invited_user_display_name]);

  const search = (q) => {
    if (!q || q.length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/social/search-users?q=${encodeURIComponent(q)}`, { withCredentials: true })
      .then((r) => setResults(r.data?.users || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const onChange = (v) => {
    setQuery(v);
    setOpen(true);
    // Clear any prior selection while the user types
    if (form.invited_user_id) {
      updateForm({ invited_user_id: '', invited_user_display_name: '' });
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 250);
  };

  const pickUser = (u) => {
    updateForm({
      invited_user_id: u.user_id,
      invited_user_display_name: u.display_name,
      invitation_email: '',
    });
    setQuery(u.display_name);
    setOpen(false);
  };

  const looksLikeEmail = /@/.test(query);

  return (
    <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-400/20 space-y-3">
      <p className="text-xs text-violet-200 font-medium">Inviter</p>
      <div className="relative">
        <label className="block text-xs text-gray-300 mb-1">
          Recherche par nom ou email
        </label>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="wizard-invitee-search"
            value={query}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => query && setOpen(true)}
            placeholder="Tapez un nom ou un email…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-400/40"
            autoComplete="off"
          />
        </div>

        {open && query.length >= 1 && (
          <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-[#0d1f35] border border-violet-400/30 shadow-xl">
            {loading && (
              <div className="p-3 text-xs text-gray-400 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Recherche…
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="p-3 text-xs text-gray-400">
                {looksLikeEmail
                  ? <>Aucun utilisateur OpenSapiens trouvé. <span className="text-violet-300">Inviter par email ci-dessous</span> ↓</>
                  : 'Aucun utilisateur. Essayez un email pour inviter quelqu\'un hors OpenSapiens.'}
              </div>
            )}
            {!loading && results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                data-testid={`wizard-invitee-result-${u.user_id}`}
                onClick={() => pickUser(u)}
                className="w-full flex items-center gap-2 p-2 hover:bg-violet-500/10 text-left"
              >
                {u.avatar ? (
                  <img src={u.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-200 text-[10px] font-medium">
                    {u.display_name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <span className="text-sm text-white">{u.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {form.invited_user_id && (
        <div
          className="flex items-center gap-2 p-2 rounded-lg bg-violet-500/15 border border-violet-400/40"
          data-testid="wizard-invitee-selected"
        >
          <Check className="h-4 w-4 text-violet-300" />
          <span className="text-xs text-violet-100">
            Invité : <strong>{form.invited_user_display_name}</strong>
          </span>
          <button
            type="button"
            onClick={() => { updateForm({ invited_user_id: '', invited_user_display_name: '' }); setQuery(''); }}
            className="ml-auto text-xs text-violet-300 hover:text-violet-100"
          >
            Changer
          </button>
        </div>
      )}

      <div className="text-center text-xs text-gray-500">— ou inviter par email —</div>
      <div>
        <label className="block text-xs text-gray-300 mb-1 flex items-center gap-1">
          <Mail className="h-3 w-3" /> Email de l'invité (hors OpenSapiens)
        </label>
        <input
          data-testid="wizard-invitee-email-input"
          type="email"
          value={form.invitation_email}
          onChange={(e) => {
            updateForm({
              invitation_email: e.target.value,
              invited_user_id: '',
              invited_user_display_name: '',
            });
            setQuery('');
          }}
          placeholder="prenom.nom@email.com"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500"
        />
      </div>
    </div>
  );
};


const EventCreationWizard = ({ isOpen, onClose, onCreated, defaultType = null, editEvent = null }) => {
  const isEdit = !!editEvent;
  const initialStep = isEdit ? 2 : (defaultType ? 2 : 1);
  const [step, setStep] = useState(initialStep);
  const [selectedType, setSelectedType] = useState(isEdit ? editEvent.type : defaultType);
  const [form, setForm] = useState(
    isEdit ? formFromEvent(editEvent) : (defaultType ? initialFormFor(defaultType) : null)
  );
  const [submitting, setSubmitting] = useState(false);
  const [createdEvent, setCreatedEvent] = useState(null);

  // Stripe LIVE (Sep 2026) — CTA « Connecter Stripe » (onboarding Express hosted)
  const connectStripe = async () => {
    try {
      const { data } = await axios.post(`${API}/stripe/connect/onboard`, {}, { withCredentials: true });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboarding Stripe indisponible.');
    }
  };
  // Sprint 1.0c V1.a-bis hotfix #4 — Bug 7 (event share in Actualité)
  const [shareToFeedState, setShareToFeedState] = useState({ checked: null, sharing: false, shared: false });

  // When the wizard opens (or switches mode), sync state to the new props.
  // useState only captures values on mount, so re-opening as Edit after a
  // previous Create session needs an explicit reset (sprint 1.0b-β fix-2).
  useEffect(() => {
    if (!isOpen) return;
    if (editEvent) {
      setStep(2);
      setSelectedType(editEvent.type);
      setForm(formFromEvent(editEvent));
      setCreatedEvent(null);
    } else if (defaultType) {
      setStep(2);
      setSelectedType(defaultType);
      setForm(initialFormFor(defaultType));
      setCreatedEvent(null);
    } else {
      setStep(1);
      setSelectedType(null);
      setForm(null);
      setCreatedEvent(null);
    }
  }, [isOpen, editEvent?.event_id, defaultType]);

  if (!isOpen) return null;

  const reset = () => {
    setStep(1);
    setSelectedType(null);
    setForm(null);
    setCreatedEvent(null);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const pickType = (typeId) => {
    setSelectedType(typeId);
    setForm(initialFormFor(typeId));
    setStep(2);
  };

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleDay = (d) => {
    setForm((f) => {
      const has = f.days_of_week.includes(d);
      return { ...f, days_of_week: has ? f.days_of_week.filter((x) => x !== d) : [...f.days_of_week, d].sort() };
    });
  };

  const buildPayload = () => {
    const payload = {
      type: form.type,
      title: form.title.trim(),
      description: form.description.trim(),
      start_time: localInputToUtcIso(form.start_local),
      end_time: localInputToUtcIso(form.end_local),
      max_capacity: form.max_capacity,
      is_free: true,
      price: 0,
      currency: 'CHF',
    };
    if (form.type === 'private_1on1') {
      if (form.invited_user_id) payload.invited_user_id = form.invited_user_id;
      if (form.invitation_email) payload.invitation_email = form.invitation_email;
    }
    if (form.type === 'public_slot' && form.recurrence_pattern === 'weekly') {
      payload.recurrence = {
        pattern: 'weekly',
        days_of_week: form.days_of_week,
        until: null,
        occurrences_count: 1,
      };
    }
    if (form.type === 'public_series') {
      payload.recurrence = {
        pattern: 'weekly',
        days_of_week: form.days_of_week,
        until: null,
        occurrences_count: form.series_occurrences,
      };
      payload.series_config = {
        can_book_full_series: form.series_can_book_full,
        can_book_per_session: form.series_can_book_per_session,
      };
    }
    return payload;
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error('Donnez un titre à votre événement.');
      return;
    }
    if (form.type === 'private_1on1' && !form.invited_user_id && !form.invitation_email) {
      toast.error('Pour un 1-to-1, indiquez un utilisateur OpenSapiens ou un email.');
      return;
    }
    if (new Date(form.end_local) <= new Date(form.start_local)) {
      toast.error('La fin doit être postérieure au début.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        // PATCH only the editable fields (server-side enforces what may change)
        const patch = {
          title: form.title.trim(),
          description: form.description.trim(),
          start_time: localInputToUtcIso(form.start_local),
          end_time: localInputToUtcIso(form.end_local),
        };
        if (form.type === 'public_event' || form.type === 'public_series') {
          patch.max_capacity = form.max_capacity;
        }
        const { data } = await axios.patch(`${API}/calendar/events/${editEvent.event_id}`, patch, { withCredentials: true });
        setCreatedEvent(data);
        setStep(3);
        onCreated?.(data);
        toast.success('Événement mis à jour.');
      } else {
        const { data } = await axios.post(`${API}/calendar/events`, buildPayload(), { withCredentials: true });
        setCreatedEvent(data);
        // Sprint 1.0c V1.a-bis hotfix #4 — Bug 7: default-check the "publier dans Actualité"
        // box for events that benefit from virality (public_event + recurring series).
        // 1on1 and slot stay unchecked (low signal for the public feed).
        const defaultShare = data.type === 'public_event' || data.type === 'recurring_series' || data.type === 'public_series';
        setShareToFeedState({ checked: defaultShare, sharing: false, shared: false });
        setStep(3);
        onCreated?.(data);
        toast.success('Événement créé !');
        // Stripe LIVE (Sep 2026) — session payante publiée sans Stripe connecté :
        // publication OK, mais on invite l'expert à finaliser l'onboarding.
        if (data?.stripe_setup_required) {
          toast.warning('Ta session est publiée. Connecte Stripe pour recevoir les paiements.', {
            duration: 12000,
            action: { label: 'Connecter Stripe', onClick: connectStripe },
          });
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? 'Mise à jour impossible.' : 'Création impossible.'));
    } finally {
      setSubmitting(false);
    }
  };

  // --------- RENDER ---------

  const renderStep1 = () => (
    <div className="space-y-4" data-testid="wizard-step-1">
      <h3 className="text-lg font-semibold text-white">Quel type d'événement ?</h3>
      <p className="text-sm text-gray-400">
        Vous pouvez créer 4 types d'événements selon votre besoin.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {TYPE_CARDS.map((card) => {
          const Icon = card.icon;
          const tc = TINT_CLASSES[card.tint];
          return (
            <button
              key={card.id}
              type="button"
              data-testid={`wizard-type-${card.id}`}
              onClick={() => pickType(card.id)}
              className={`text-left p-4 rounded-xl border bg-white/5 hover:${tc.bg} hover:${tc.border} border-white/10 transition-all group`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-2 rounded-lg ${tc.bg} ${tc.text}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className={`font-semibold ${tc.text}`}>{card.label}</span>
              </div>
              <p className="text-xs text-gray-300/80">{card.description}</p>
              <p className="text-[11px] text-gray-500 mt-2"><em>{card.examples}</em></p>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep2 = () => {
    if (!form) return null;
    const tc = TINT_CLASSES[TYPE_CARDS.find((c) => c.id === form.type).tint];
    return (
      <div className="space-y-4" data-testid="wizard-step-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-xs rounded-full ${tc.bg} ${tc.text} border ${tc.border}`}>
            {TYPE_CARDS.find((c) => c.id === form.type).label}
          </span>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Titre *</label>
          <input
            data-testid="wizard-title-input"
            value={form.title}
            onChange={(e) => updateForm({ title: e.target.value })}
            placeholder="Ex : Mentorat product senior"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Description (optionnel)</label>
          <textarea
            data-testid="wizard-description-input"
            value={form.description}
            onChange={(e) => updateForm({ description: e.target.value })}
            placeholder="Ce que les participants apprendront…"
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-sm resize-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DateTimePickerFr
            label="Début"
            testId="wizard-start"
            value={form.start_local ? new Date(form.start_local) : null}
            onChange={(d) => {
              // Sprint 1.0c Jun 9 — auto-align end date/time when start changes.
              //
              // Rule (cf. brief): when the user picks a new start, snap the end
              // to the same calendar day with a duration of 1h (the default for
              // 1-to-1 + slot + event + series). If the start has changed by
              // less than 1h compared to the previous start, we keep the user's
              // explicit end duration intact (they probably nudged the start
              // and don't want their carefully picked end to move).
              if (!d) {
                updateForm({ start_local: '' });
                return;
              }
              const prevStart = form.start_local ? new Date(form.start_local) : null;
              const prevEnd = form.end_local ? new Date(form.end_local) : null;
              const next = { start_local: toLocalInputValue(d) };

              const sameDay = prevStart && d.getFullYear() === prevStart.getFullYear()
                && d.getMonth() === prevStart.getMonth()
                && d.getDate() === prevStart.getDate();

              // Preserve duration only if start moved within the same day (user
              // just tweaked the hour). Otherwise reset to start + 1h.
              if (sameDay && prevStart && prevEnd && prevEnd > prevStart) {
                const durationMs = prevEnd.getTime() - prevStart.getTime();
                const newEnd = new Date(d.getTime() + durationMs);
                // If duration push crosses midnight, clamp to 23:59 of same day.
                if (newEnd.getDate() !== d.getDate() || newEnd.getMonth() !== d.getMonth()) {
                  const clamped = new Date(d);
                  clamped.setHours(23, 59, 0, 0);
                  next.end_local = toLocalInputValue(clamped);
                } else {
                  next.end_local = toLocalInputValue(newEnd);
                }
              } else {
                // Day change OR initial pick → end = start + 1h, clamp at 23:59.
                const newEnd = new Date(d.getTime() + 60 * 60 * 1000);
                if (newEnd.getDate() !== d.getDate() || newEnd.getMonth() !== d.getMonth()) {
                  const clamped = new Date(d);
                  clamped.setHours(23, 59, 0, 0);
                  next.end_local = toLocalInputValue(clamped);
                } else {
                  next.end_local = toLocalInputValue(newEnd);
                }
              }
              updateForm(next);
            }}
          />
          <DateTimePickerFr
            label="Fin"
            testId="wizard-end"
            value={form.end_local ? new Date(form.end_local) : null}
            onChange={(d) => updateForm({ end_local: toLocalInputValue(d) })}
          />
        </div>

        {/* Fix timezone Jul 2026 — badge de réassurance : la saisie est en heure
            locale du user et convertie en UTC côté serveur. */}
        <p className="flex items-center gap-1.5 text-[11px] text-cyan-300/80" data-testid="wizard-timezone-badge">
          <Globe className="h-3 w-3 flex-shrink-0" />
          Heure locale ({guessUserTz()}) — vos participants la verront dans leur propre fuseau horaire
        </p>

        {/* Type-specific fields */}
        {form.type === 'private_1on1' && (
          <InviteePicker
            form={form}
            updateForm={updateForm}
          />
        )}

        {form.type === 'public_event' && (
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Capacité maximale</label>
            <input
              data-testid="wizard-capacity-input"
              type="number"
              min="1"
              max="200"
              value={form.max_capacity}
              onChange={(e) => updateForm({ max_capacity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              className="w-32 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
            />
          </div>
        )}

        {form.type === 'public_slot' && (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-300">Récurrence</label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="wizard-recurrence-none"
                onClick={() => updateForm({ recurrence_pattern: 'none' })}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  form.recurrence_pattern === 'none'
                    ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200'
                    : 'border-white/10 text-gray-400'
                }`}
              >
                Ponctuel (une seule fois)
              </button>
              <button
                type="button"
                data-testid="wizard-recurrence-weekly"
                onClick={() => updateForm({ recurrence_pattern: 'weekly' })}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  form.recurrence_pattern === 'weekly'
                    ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200'
                    : 'border-white/10 text-gray-400'
                }`}
              >
                Hebdomadaire (tous les…)
              </button>
            </div>
            {form.recurrence_pattern === 'weekly' && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {DAYS.map((d) => {
                  const on = form.days_of_week.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      data-testid={`wizard-day-${d.value}`}
                      onClick={() => toggleDay(d.value)}
                      className={`w-10 h-9 rounded-lg text-xs border ${
                        on ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200' : 'border-white/10 text-gray-400'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {form.type === 'public_series' && (
          <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-400/20 space-y-3">
            <p className="text-xs text-cyan-200 font-medium">Configuration de la série</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-300 mb-1">Nombre de séances</label>
                <input
                  data-testid="wizard-series-occurrences"
                  type="number"
                  min="2"
                  max="52"
                  value={form.series_occurrences}
                  onChange={(e) => updateForm({ series_occurrences: Math.max(2, parseInt(e.target.value, 10) || 2) })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-300 mb-1">Capacité par séance</label>
                <input
                  data-testid="wizard-series-capacity"
                  type="number"
                  min="1"
                  max="200"
                  value={form.max_capacity}
                  onChange={(e) => updateForm({ max_capacity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = form.days_of_week.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className={`w-10 h-9 rounded-lg text-xs border ${
                      on ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200' : 'border-white/10 text-gray-400'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-xs text-gray-300">
                <input
                  data-testid="wizard-series-full-booking"
                  type="checkbox"
                  checked={form.series_can_book_full}
                  onChange={(e) => updateForm({ series_can_book_full: e.target.checked })}
                />
                Permettre l'inscription à la série entière
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-300">
                <input
                  data-testid="wizard-series-per-session"
                  type="checkbox"
                  checked={form.series_can_book_per_session}
                  onChange={(e) => updateForm({ series_can_book_per_session: e.target.checked })}
                />
                Permettre l'inscription séance par séance
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-5 text-center py-6" data-testid="wizard-step-3">
      <div className="mx-auto w-16 h-16 rounded-full bg-cyan-500/15 border border-cyan-400/40 flex items-center justify-center">
        <Check className="h-8 w-8 text-cyan-300" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-white">{isEdit ? 'Événement mis à jour' : 'Événement créé'}</h3>
        <p className="text-sm text-gray-400 mt-1">Il est désormais visible dans votre calendrier.</p>
      </div>

      {createdEvent && (
        <div className="mx-auto max-w-md p-4 rounded-xl bg-white/5 border border-white/10 text-left space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className="h-4 w-4 text-cyan-400" />
            <span className="text-white font-medium">{createdEvent.title}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            {parseUtc(createdEvent.start_time)?.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
            {' → '}
            {parseUtc(createdEvent.end_time)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
          {/* Stripe LIVE (Sep 2026) — session payante publiée sans Stripe connecté */}
          {createdEvent.stripe_setup_required && (
            <div
              className="mt-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30"
              data-testid="wizard-stripe-setup-banner"
            >
              <p className="text-xs text-amber-300 font-semibold">
                Ta session est publiée. Connecte Stripe pour recevoir les paiements.
              </p>
              <p className="text-[11px] text-amber-200/70 mt-1">
                En attendant, les visiteurs voient « Paiement en cours de configuration ».
                100% du montant te revient — aucune commission OpenSapiens.
              </p>
              <button
                type="button"
                onClick={connectStripe}
                data-testid="wizard-connect-stripe-btn"
                className="mt-2 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold transition-colors"
              >
                Connecter Stripe
              </button>
            </div>
          )}
          {createdEvent.visio_link && (
            <div className="pt-2">
              <p className="text-xs text-gray-400 mb-1">Lien visio (Jitsi) :</p>
              <a
                href={createdEvent.visio_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wizard-jitsi-link"
                className="text-xs text-cyan-300 underline break-all"
              >
                {createdEvent.visio_link}
              </a>
            </div>
          )}
          <div className="pt-2 text-xs text-gray-400">
            ICS :{' '}
            <a
              href={`${API}/calendar/events/${createdEvent.event_id}/ics${createdEvent.visibility === 'private' ? `?token=${createdEvent.ics_token}` : ''}`}
              data-testid="wizard-ics-link"
              className="text-cyan-300 underline"
            >
              Ajouter à mon calendrier
            </a>
          </div>

          {/* Sprint 1.0c V1.a-bis hotfix #4 — Bug 7 : publier dans Actualité */}
          {createdEvent.type !== 'private_1on1' && (
            <div className="pt-3 mt-2 border-t border-white/10">
              {shareToFeedState.shared ? (
                <p className="text-xs text-emerald-300 flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5" /> Partagé dans Actualité
                </p>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!shareToFeedState.checked}
                      onChange={(e) => setShareToFeedState((s) => ({ ...s, checked: e.target.checked }))}
                      className="rounded border-white/20 bg-white/5"
                      data-testid="wizard-share-to-feed-checkbox"
                    />
                    Publier aussi dans l'Actualité (visibilité du feed public)
                  </label>
                  {shareToFeedState.checked && (
                    <button
                      type="button"
                      disabled={shareToFeedState.sharing}
                      onClick={async () => {
                        setShareToFeedState((s) => ({ ...s, sharing: true }));
                        try {
                          await axios.post(
                            `${API}/calendar/events/${createdEvent.event_id}/share-to-feed`,
                            { message: `📅 ${createdEvent.title}` },
                            { withCredentials: true }
                          );
                          setShareToFeedState({ checked: true, sharing: false, shared: true });
                          toast.success('Événement partagé dans Actualité !');
                        } catch (e) {
                          setShareToFeedState((s) => ({ ...s, sharing: false }));
                          toast.error(e.response?.data?.detail || 'Partage impossible');
                        }
                      }}
                      data-testid="wizard-share-to-feed-btn"
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-cyan-500 to-violet-500 text-white disabled:opacity-50"
                    >
                      {shareToFeedState.sharing ? 'Partage…' : 'Partager dans Actualité'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="event-wizard-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-gradient-to-br from-[#0d1f35] via-[#0f2540] to-[#0d1f35] border border-white/10 shadow-2xl p-6 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">
              {isEdit ? 'Modifier l\'événement' : 'Nouvel événement'}
            </h2>
          </div>
          <button data-testid="wizard-close-btn" onClick={close} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 flex-shrink-0">
          {[1, 2, 3].map((i) => (
            <React.Fragment key={i}>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                  step === i
                    ? 'bg-cyan-500/20 border border-cyan-400/60 text-cyan-200'
                    : step > i
                    ? 'bg-cyan-500/40 text-white'
                    : 'bg-white/5 text-gray-500 border border-white/10'
                }`}
              >
                {step > i ? <Check className="h-3.5 w-3.5" /> : i}
              </div>
              {i < 3 && <div className="flex-1 h-px bg-white/10" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body — Sprint 1.0c V1.a-bis hotfix #5 (Bug F): scrollable interior to keep the time picker visible. */}
        <div className="min-h-[300px] flex-1 overflow-y-auto pr-1">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
          <div>
            {step === 2 && !defaultType && !isEdit && (
              <button
                data-testid="wizard-back-btn"
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" /> Type
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {step !== 3 && (
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg text-sm border border-white/10 text-gray-300 hover:bg-white/5"
              >
                Annuler
              </button>
            )}
            {step === 2 && (
              <button
                data-testid="wizard-create-btn"
                type="button"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-cyan-500 to-violet-500 text-white font-medium hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {submitting ? (isEdit ? 'Enregistrement…' : 'Création…') : (isEdit ? 'Enregistrer' : 'Créer')}
              </button>
            )}
            {step === 3 && (
              <button
                data-testid="wizard-done-btn"
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-cyan-500 to-violet-500 text-white font-medium"
              >
                Terminer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCreationWizard;
